/**
 * SoftChip — chip "soft tinted" coerente col pattern usato nei tab admin
 * dell'app utente (es. `TenantModulesAddonTab`, `TenantModuleVisibilityTab`).
 *
 * Pattern: bgcolor = alpha(palette[tone].main, 0.12), color = palette[tone].dark.
 * Variante `mono` per id/codici tecnici (monospace + tracking leggero).
 */

import React from 'react';
import { Chip, alpha, useTheme } from '@mui/material';

export type SoftChipTone =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'primary'
  | 'gold'
  | 'default';

export interface SoftChipProps {
  label: React.ReactNode;
  tone?: SoftChipTone;
  icon?: React.ReactElement;
  mono?: boolean;
  /** Variante outlined (tratteggiato per stati "non disponibile") */
  outlined?: boolean;
}

export const SoftChip: React.FC<SoftChipProps> = ({
  label,
  tone = 'default',
  icon,
  mono = false,
  outlined = false,
}) => {
  const theme = useTheme();

  const palette = theme.palette;
  const colorMap: Record<SoftChipTone, { bg: string; fg: string; border: string }> = {
    success: {
      bg: alpha(palette.success.main, 0.12),
      fg: palette.success.dark,
      border: alpha(palette.success.main, 0.35),
    },
    warning: {
      bg: alpha(palette.warning.main, 0.14),
      fg: palette.warning.dark,
      border: alpha(palette.warning.main, 0.40),
    },
    error: {
      bg: alpha(palette.error.main, 0.12),
      fg: palette.error.dark,
      border: alpha(palette.error.main, 0.35),
    },
    info: {
      bg: alpha(palette.info.main, 0.10),
      fg: palette.info.dark,
      border: alpha(palette.info.main, 0.35),
    },
    primary: {
      bg: alpha(palette.primary.main, 0.10),
      fg: palette.primary.dark,
      border: alpha(palette.primary.main, 0.35),
    },
    // Dorato "distintivo" (es. founding partner): non esiste come colore
    // semantico nella palette MUI → giallo oro esplicito, distinto dal
    // warning ambra.
    gold: {
      bg: alpha('#EAB308', 0.16),
      fg: '#854D0E',
      border: alpha('#CA8A04', 0.55),
    },
    default: {
      bg: alpha(palette.text.primary, 0.06),
      fg: palette.text.secondary,
      border: alpha(palette.text.primary, 0.18),
    },
  };
  const { bg, fg, border } = colorMap[tone];

  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      sx={{
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 600,
        bgcolor: outlined ? 'transparent' : bg,
        color: fg,
        border: outlined ? `1px dashed ${border}` : 'none',
        fontFamily: mono ? 'monospace' : undefined,
        letterSpacing: mono ? '0.02em' : undefined,
        '& .MuiChip-icon': { color: fg, ml: '6px', fontSize: 14 },
      }}
    />
  );
};

export default SoftChip;
